/**
 * InboundSmsToastHost — bottom-LEFT toast when a lead texts the CRM. Mounted
 * ONCE in CRMApp next to <CallbackReminderHost /> (outside <Routes>, so it
 * survives navigation and runs on every CRM page).
 *
 * Listens on the fixed Supabase Realtime broadcast topic "crm:inbound-sms"
 * (event "sms_inbound", payload { contactId, ownerUserId }) — mirrors
 * CommsThread's per-contact subscribe/cleanup exactly, just on a fixed topic.
 * The broadcast is content-free (no PHI): on a passing event we fetch the
 * preview through the authed, RLS-aware API before showing anything.
 *
 * Visibility (defense in depth — the backend enforces it too): recruiters
 * (basic_user) only see toasts for UNASSIGNED leads or leads assigned to them;
 * super_admin / administrator see all. Filtered client-side off the broadcast
 * payload first, then re-checked server-side (a 403 is silently ignored).
 *
 * z-30: same layer as CallbackReminderHost — below the rating toast (z-40) and
 * softphone (z-50) — but bottom-LEFT so it clears the bottom-right toasts and
 * the bottom-left ParkedCallsPanel is z-40 (above this).
 */
import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { MessageSquare, X } from 'lucide-react';
import api from '../../services/api';
import { subscribeInboundSms } from '../../lib/realtimeTransport';
import { useAuth } from '../../contexts/AuthContext';

const AUTO_DISMISS_MS = 15_000;
const MAX_TOASTS = 4;

interface InboundToast {
  key: string; // contactId + occurredAt — also the de-dupe key
  contactId: string;
  name: string | null;
  preview: string | null;
  occurredAt: string | null;
}

interface InboundPreview {
  contact_id: string;
  name: string | null;
  preview: string | null;
  owner_user_id: string | null;
  occurred_at: string | null;
}

export default function InboundSmsToastHost() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [toasts, setToasts] = useState<InboundToast[]>([]);
  // Every (contactId + occurredAt) we've ever shown, so a missed broadcast or a
  // re-render never double-toasts the same message.
  const shownKeysRef = useRef<Set<string>>(new Set());

  // Keep the latest user in a ref so the (stable, once-only) subscription's
  // callback can read the current id/role without resubscribing per render.
  const userRef = useRef(user);
  userRef.current = user;

  const dismiss = (key: string) => setToasts((prev) => prev.filter((t) => t.key !== key));

  const openContact = (t: InboundToast) => {
    dismiss(t.key);
    navigate(`/crm/contacts/${t.contactId}`);
  };

  // Subscribe once to the fixed inbound-SMS topic through the realtime transport
  // adapter (WP-3.3). Under the default 'supabase' transport this is byte-identical
  // to the previous supabase.channel('crm:inbound-sms').on('broadcast', ...); under
  // 'sse' the same ids-only payload arrives over the shared EventSource. The
  // visibility gate + preview fetch below are unchanged.
  useEffect(() => {
    const unsubscribe = subscribeInboundSms(async (payload) => {
      const contactId = payload.contactId;
      if (!contactId) return;

      // Client-side visibility gate (the server re-checks; this just avoids a
      // pointless fetch). Recruiters only see unassigned / own leads.
      const u = userRef.current;
      const ownerUserId = payload.ownerUserId ?? null;
      const canSee =
        u?.role === 'super_admin' ||
        u?.role === 'administrator' ||
        ownerUserId == null ||
        ownerUserId === u?.id;
      if (!canSee) return;

      try {
        const res = await api.get<InboundPreview>(`/api/crm/contacts/${contactId}/inbound-preview`);
        const data = res.data;
        if (!data) return;
        const key = `${data.contact_id}:${data.occurred_at ?? ''}`;
        if (shownKeysRef.current.has(key)) return;
        shownKeysRef.current.add(key);
        setToasts((prev) => {
          const next = [
            ...prev,
            {
              key,
              contactId: data.contact_id,
              name: data.name,
              preview: data.preview,
              occurredAt: data.occurred_at,
            },
          ];
          // Cap the stack: drop the oldest so we never show more than MAX_TOASTS.
          return next.slice(-MAX_TOASTS);
        });
      } catch {
        /* 403 (not visible) or any error — silently ignore, never toast */
      }
    });
    return unsubscribe;
  }, []);

  // Auto-dismiss each toast ~15s after it appears. One timer per toast key.
  useEffect(() => {
    if (!toasts.length) return;
    const timers = toasts.map((t) =>
      window.setTimeout(() => dismiss(t.key), AUTO_DISMISS_MS),
    );
    return () => timers.forEach((id) => window.clearTimeout(id));
  }, [toasts]);

  if (!toasts.length) return null;

  return (
    <div className="fixed bottom-4 left-4 z-30 flex w-80 max-w-[calc(100vw-2rem)] flex-col gap-2">
      {toasts.map((t) => (
        <div
          key={t.key}
          role="button"
          tabIndex={0}
          onClick={() => openContact(t)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && e.target === e.currentTarget) openContact(t);
          }}
          className="cursor-pointer rounded-xl border border-gray-200 bg-white p-4 text-left shadow-lg transition-colors duration-150 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-primary-500 dark:border-gray-700 dark:bg-gray-800 dark:hover:bg-gray-700/60"
        >
          <div className="flex items-start justify-between gap-2">
            <div className="flex min-w-0 items-start gap-2">
              <MessageSquare className="mt-0.5 h-4 w-4 shrink-0 text-primary-600 dark:text-primary-400" />
              <div className="min-w-0">
                <p className="text-sm font-semibold text-gray-900 dark:text-gray-100">
                  {t.name || 'New text message'}
                </p>
                {t.preview && (
                  <p className="mt-0.5 line-clamp-2 text-sm text-gray-700 dark:text-gray-300">
                    {t.preview}
                  </p>
                )}
              </div>
            </div>
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                dismiss(t.key);
              }}
              aria-label="Dismiss message"
              className="inline-flex min-h-[44px] min-w-[44px] shrink-0 items-center justify-center rounded text-gray-400 transition-colors duration-150 hover:text-gray-600 dark:hover:text-gray-300 sm:min-h-0 sm:min-w-0 sm:p-0.5"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
        </div>
      ))}
    </div>
  );
}
